import React from "react";
import FilterHeader from "@/components/campaigns/FilterHeader";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import Table from "@/components/ui/shared/table/Table";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableRow from "@/components/ui/shared/table/TableRow";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import CampaignEdit from "@/components/campaigns/CampaignEdit";
import { useCampaigns } from "@/hooks/useCampaigns";
import { useAssistants } from "@/hooks/useAssistants";
import { usePipelines } from "@/hooks/usePipelines";
import { useState, useEffect, memo } from "react";

interface CampaignsProps {}

type AccountCampaign = {
  id: string;
  account_id: string;
  name: string;
  assistant_id: string | null;
  trigger_type: string;
  trigger_keywords: string[] | null;
  tags: string[] | null;
  pipeline_id: string | null;
  pipeline_stage_id: string | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
};

type Assistant = {
  id: string;
  name: string;
  is_active: boolean;
};

type Pipeline = {
  id: string;
  name: string;
};

type PipelineStage = {
  id: string;
  name: string;
  pipeline_id: string;
};

interface CampaignRowProps {
  campaign: AccountCampaign;
  assistants: Assistant[];
  pipelines: Pipeline[];
  pipelineStages: PipelineStage[];
  onToggleStatus: (id: string, currentStatus: boolean | null) => void;
}

const CampaignRow = memo<CampaignRowProps>(({
  campaign,
  assistants,
  pipelines,
  pipelineStages,
  onToggleStatus
}) => {
  const getAssistantName = (assistantId: string | null) => {
    if (!assistantId) return 'Nenhum assistente';
    const assistant = assistants.find(a => a.id === assistantId);
    return assistant ? assistant.name : 'Assistente não encontrado';
  };

  const getPipelineName = (pipelineId: string | null) => {
    if (!pipelineId) return 'Nenhum pipeline';
    const pipeline = pipelines.find(p => p.id === pipelineId);
    return pipeline ? pipeline.name : 'Pipeline não encontrado';
  };

  const getStageName = (stageId: string | null) => {
    if (!stageId) return 'Nenhuma etapa';
    const stage = pipelineStages.find(s => s.id === stageId);
    return stage ? stage.name : 'Etapa não encontrada';
  };

  const getTriggerDescription = (campaign: AccountCampaign) => {
    if (campaign.trigger_type === 'first_contact') {
      return 'Primeiro Contato';
    }

    if (campaign.trigger_type === 'any') {
      return 'Qualquer mensagem';
    }

    const typeText = campaign.trigger_type === 'contains' ? 'Contém' : 'Inicia com';
    const keywords = campaign.trigger_keywords && campaign.trigger_keywords.length > 0
      ? campaign.trigger_keywords.slice(0, 2).join(', ') +
        (campaign.trigger_keywords.length > 2 ? '...' : '')
      : 'Nenhuma palavra-chave';

    return `${typeText}: ${keywords}`;
  };

  const assistantName = getAssistantName(campaign.assistant_id);
  const pipelineName = getPipelineName(campaign.pipeline_id);
  const stageName = getStageName(campaign.pipeline_stage_id);
  const triggerDescription = getTriggerDescription(campaign);
  const createdDate = campaign.created_at
    ? new Date(campaign.created_at).toLocaleDateString('pt-BR')
    : 'Data não disponível';

  return (
    <TableRow>
      <TableCell as="td">
        <div className="flex flex-col">
          <p className="font-medium">{campaign.name}</p>
          <p className="text-xs text-neutral-400">Criada em {createdDate}</p>
        </div>
      </TableCell>

      <TableCell as="td">
        <div className="flex flex-col">
          <span className="text-neutral-50 text-sm">{assistantName}</span>
          {campaign.assistant_id && (
            <span className="text-neutral-400 text-xs">
              {assistants.find(a => a.id === campaign.assistant_id)?.is_active ? 'Ativo' : 'Inativo'}
            </span>
          )}
        </div>
      </TableCell>

      <TableCell as="td">
        <div className="flex flex-col">
          <span className="text-neutral-50 text-sm">{triggerDescription}</span>
          {campaign.trigger_keywords && campaign.trigger_keywords.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {campaign.trigger_keywords.slice(0, 3).map((keyword: string, index: number) => (
                <Badge key={index} variant="default2" className="text-xs">
                  {keyword}
                </Badge>
              ))}
              {campaign.trigger_keywords.length > 3 && (
                <Badge variant="default2" className="text-xs">
                  +{campaign.trigger_keywords.length - 3}
                </Badge>
              )}
            </div>
          )}
        </div>
      </TableCell>

      <TableCell as="td">
        <div className="flex flex-col">
          <span className="text-neutral-50 text-sm">{pipelineName}</span>
          {campaign.pipeline_stage_id && (
            <span className="text-neutral-400 text-xs">{stageName}</span>
          )}
        </div>
      </TableCell>

      <TableCell as="td">
        <div className="flex items-center gap-2">
          <div
            className={`w-[7px] h-[7px] rounded-full ${
              campaign.is_active ? "bg-green-500" : "bg-orange-400"
            }`}
          />
          <span className="text-sm">
            {campaign.is_active ? "Ativa" : "Inativa"}
          </span>
        </div>
      </TableCell>

      <TableCell as="td">
        <Switch
          checked={campaign.is_active || false}
          onCheckedChange={() => onToggleStatus(campaign.id, campaign.is_active)}
          activeColor="data-[state=checked]:bg-[#22c55e]"
          inactiveColor="data-[state=unchecked]:bg-[#3F3F46]"
          thumbColor="bg-white"
        />
      </TableCell>

      <TableCell as="td">
        <CampaignEdit campaign={campaign} />
      </TableCell>
    </TableRow>
  );
});

CampaignRow.displayName = 'CampaignRow';

const Campaigns: React.FC<CampaignsProps> = () => {
  const { campaigns, loading, error, toggleCampaignStatus } = useCampaigns();
  const { assistants } = useAssistants();
  const { pipelines, pipelineStages } = usePipelines();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCampaigns, setFilteredCampaigns] = useState(campaigns);

  // Filter campaigns based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCampaigns(campaigns);
      return;
    }

    const filtered = campaigns.filter(campaign => {
      const name = campaign.name.toLowerCase();
      const search = searchTerm.toLowerCase();

      return name.includes(search) ||
             campaign.trigger_keywords?.some((keyword: string) =>
               keyword.toLowerCase().includes(search)
             );
    });

    console.log('🔍 [Campaigns] Filtering campaigns:', {
      searchTerm,
      total_campaigns: campaigns.length,
      filtered_count: filtered.length
    });

    setFilteredCampaigns(filtered);
  }, [searchTerm, campaigns]);

  const handleToggleStatus = async (id: string, currentStatus: boolean | null) => {
    await toggleCampaignStatus(id, !currentStatus);
  };

  if (loading) {
    return (
      <div className="w-full flex h-[calc(100dvh-110px)] flex-col">
        <h2 className="text-[26px] mb-[10px] sm:text-[30px] font-semibold text-neutral-50">Campanhas</h2>
        <FilterHeader onSearchChange={setSearchTerm} />
        <div className="flex-1 min-h-0 mt-3 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
            <p className="text-neutral-50">Carregando campanhas...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex h-[calc(100dvh-110px)] flex-col">
        <h2 className="text-[26px] mb-[10px] sm:text-[30px] font-semibold text-neutral-50">Campanhas</h2>
        <FilterHeader onSearchChange={setSearchTerm} />
        <div className="flex-1 min-h-0 mt-3 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-50 mb-2">Erro ao carregar campanhas</h3>
            <p className="text-neutral-300 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex h-[calc(100dvh-110px)]  flex-col">
      <h2 className="text-[26px] mb-[10px] sm:text-[30px] font-semibold text-neutral-50">Campanhas</h2>
      <FilterHeader onSearchChange={setSearchTerm} />
      <div className="flex-1 min-h-0 mt-3">
        {campaigns.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center border-[1px] border-[#3F3F464D] rounded-[12px] bg-gray-500">
            <div className="flex flex-col items-center gap-4 text-center p-6">
              <div className="w-16 h-16 bg-neutral-600 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-50 mb-2">Nenhuma campanha encontrada</h3>
                <p className="text-neutral-300 text-sm">
                  Crie sua primeira campanha para automatizar o atendimento.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <ScrollArea
            classNames={{
              root: "w-full h-full overflow-auto border-[1px] border-[#3F3F464D] rounded-[12px] bg-gray-500",
            }}
          >
            <Table className="min-w-[900px]">
              <TableHeader className="border-b-[1px] border-[#3F3F464D]">
                <TableRow>
                  {["Campanha", "Assistente", "Gatilho", "Pipeline", "Status", "Ativa", "Ações"].map((item, index) => (
                    <TableCell key={index} as="th">
                      <div className="flex justify-start items-center gap-1">
                        <p>{item}</p>
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCampaigns.map((campaign) => (
                  <CampaignRow
                    key={campaign.id}
                    campaign={campaign}
                    assistants={assistants}
                    pipelines={pipelines}
                    pipelineStages={pipelineStages}
                    onToggleStatus={handleToggleStatus}
                  />
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

export default Campaigns;